import { strToU8 } from 'fflate';
import { browserZipLimit, createBoundedZip } from '../../utils/boundedZip';
import type {
  MultiPackageOutputManifestItem,
  MultiPackagePrintResult,
  MultiPackageRecoveryManifest,
} from '../../types/multiPackagePrint';
import { createPrintRunOutputArtifacts, type PrintRunArtifact } from '../../utils/downloadPrintRunOutput';
import { createPrintRunCsvArtifact } from '../../utils/exportPrintRunJob';

export type MultiPackageZipArtifact = PrintRunArtifact & { includedFiles: string[] };

function safeName(value: string, fallback: string): string {
  const sanitized = value
    .split('')
    .map((character) => character.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(character) ? '-' : character)
    .join('')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[. -]+|[. -]+$/g, '')
    .slice(0, 180);
  return sanitized || fallback;
}

function extension(fileName: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(fileName);
  return match ? `.${match[1].toLowerCase()}` : '.bin';
}

function uniqueName(desired: string, used: Set<string>): string {
  const dot = desired.lastIndexOf('.');
  const base = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : '';
  let candidate = desired;
  let suffix = 2;
  while (used.has(candidate.toLowerCase())) candidate = `${base}-${suffix++}${ext}`;
  used.add(candidate.toLowerCase());
  return candidate;
}

function blobBytes(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Output artifact could not be read'));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(blob);
  });
}

export function buildRecoveryManifest(result: MultiPackagePrintResult): MultiPackageRecoveryManifest | null {
  const incomplete = result.items.filter((item) => item.status !== 'Completed'
    && !(item.stage === 'OutputPending' && result.outputManifest.items.some((entry) => entry.sequence === item.sequence)))
    .map((item) => ({
    sequence: item.sequence,
    packageLabel: item.packageLabel || `METRC package ${item.metrcPackageId}`,
    status: item.status as Exclude<typeof item.status, 'Completed'>,
    stage: item.stage,
    message: item.errorMessage,
  }));
  return incomplete.length ? {
    kind: 'multi-package-partial-recovery-manifest',
    version: 1,
    batchId: result.batchId,
    status: result.status,
    incomplete,
  } : null;
}

function validateManifestItem(result: MultiPackagePrintResult, manifestItem: MultiPackageOutputManifestItem) {
  const item = result.items.find((candidate) => candidate.sequence === manifestItem.sequence);
  if (!item || !((item.status === 'Completed' && item.stage === 'Completed')
    || (item.status === 'RecoveryRequired' && item.stage === 'OutputPending'))
    || item.printRunId !== manifestItem.printRunId
    || item.packageCoaId !== manifestItem.packageCoaId || item.packageLabel !== manifestItem.packageLabel
    || item.issuanceId !== manifestItem.issuanceId || item.count !== manifestItem.count
    || item.offset !== manifestItem.offset || manifestItem.endExclusive !== manifestItem.offset + manifestItem.count
    || item.range?.length !== 1 || item.range[0]?.[0] !== manifestItem.offset
    || item.range[0]?.[1] !== manifestItem.endExclusive - 1) {
    throw new Error(`Output manifest does not match completed child ${manifestItem.packageLabel}`);
  }
}

function requestedFormats(includedFormats: string[]) {
  const formats = new Set(includedFormats.map((format) => format.trim().toLowerCase()));
  const supported = new Set(['pdf', 'zpl', 'output', 'csv', 'reels']);
  const unsupported = [...formats].filter((format) => !supported.has(format));
  if (unsupported.length > 0) throw new Error(`Unsupported ZIP output format: ${unsupported.join(', ')}`);
  const output = formats.has('pdf') || formats.has('zpl') || formats.has('output');
  const csv = formats.has('csv');
  const reels = formats.has('reels');
  if (!output && !csv && !reels) throw new Error('Output manifest does not request a supported ZIP format');
  if ((formats.has('pdf') && formats.has('zpl')) || (reels && formats.has('zpl')))
    throw new Error('Output manifest requests incompatible label formats');
  return { output: output || reels, csv, reels, extension: formats.has('zpl') ? '.zpl' : formats.has('pdf') || reels ? '.pdf' : null };
}

export async function createMultiPackageZip(
  organizationId: number,
  result: MultiPackagePrintResult,
): Promise<MultiPackageZipArtifact> {
  if (result.estimatedZipBytesOverLimit != null && result.estimatedZipBytesOverLimit > 0) {
    throw new Error(`Estimated ZIP exceeds the safety limit by ${result.estimatedZipBytesOverLimit} bytes`);
  }
  const formats = requestedFormats(result.outputManifest.includedFormats);
  const sequences = new Set<number>();
  const eligible = result.items.filter((item) => item.status === 'Completed'
    || item.status === 'RecoveryRequired' && item.stage === 'OutputPending');
  if (eligible.length !== result.outputManifest.items.length
    || result.outputManifest.items.some((item) => !eligible.some((child) => child.sequence === item.sequence))) {
    throw new Error('Output manifest does not list every ready child');
  }

  const files: Record<string, Uint8Array> = {};
  const maxBytes = browserZipLimit(result.safetyLimits.maxEstimatedZipBytes);
  const used = new Set<string>();
  const usedPrefixes = new Set<string>();
  let totalUncompressedBytes = 0;
  const addFile = (name: string, bytes: Uint8Array) => {
    totalUncompressedBytes += bytes.byteLength;
    if (totalUncompressedBytes > maxBytes) {
      throw new Error(`ZIP content exceeds the safety limit by ${totalUncompressedBytes - maxBytes} bytes`);
    }
    files[name] = bytes;
  };
  for (const item of [...result.outputManifest.items].sort((a, b) => a.sequence - b.sequence)) {
    if (sequences.has(item.sequence)) throw new Error(`Output manifest contains duplicate child sequence ${item.sequence}`);
    sequences.add(item.sequence);
    validateManifestItem(result, item);
    const request = {
      id: item.printRunId,
      organizationId,
      jobName: item.fileNamePrefix,
      packageLabel: item.packageLabel,
      packageCoaId: item.packageCoaId,
      expectedChildOutput: {
        count: item.count, offset: item.offset, endExclusive: item.endExclusive,
      },
    };
    const output = formats.output ? await createPrintRunOutputArtifacts(organizationId, request) : null;
    const csv = formats.csv ? await createPrintRunCsvArtifact(request) : null;
    const originalPrefix = safeName(item.fileNamePrefix, `child-${item.sequence + 1}`);
    let prefix = originalPrefix;
    let suffix = 2;
    while (usedPrefixes.has(prefix.toLowerCase())) prefix = `${originalPrefix}-${suffix++}`;
    usedPrefixes.add(prefix.toLowerCase());
    if (output) {
      if (!output.length || formats.reels !== (output.length > 1 || /-reel-1\.pdf$/i.test(output[0]?.fileName ?? ''))) {
        throw new Error(`Output reel settings do not match completed child ${item.packageLabel}`);
      }
      for (let index = 0; index < output.length; index++) {
        if (totalUncompressedBytes + output[index].blob.size > maxBytes)
          throw new Error(`ZIP content exceeds the safety limit by ${totalUncompressedBytes + output[index].blob.size - maxBytes} bytes`);
        const outputExtension = extension(output[index].fileName);
        if (formats.extension && outputExtension !== formats.extension) {
          throw new Error(`Output format does not match completed child ${item.packageLabel}`);
        }
        const outputName = uniqueName(`${prefix}${formats.reels ? `-reel-${index + 1}` : ''}${outputExtension}`, used);
        addFile(outputName, await blobBytes(output[index].blob));
      }
    }
    if (csv) {
      const csvName = uniqueName(`${prefix}.csv`, used);
      addFile(csvName, await blobBytes(csv.blob));
    }
  }

  const recovery = buildRecoveryManifest(result);
  if (recovery) {
    const name = uniqueName('partial-recovery-manifest.json', used);
    addFile(name, strToU8(JSON.stringify(recovery, null, 2)));
  }
  const bytes = await createBoundedZip(files, maxBytes);
  const blob = new Blob([new Uint8Array(bytes).buffer], { type: 'application/zip' });
  if (blob.size > result.safetyLimits.maxEstimatedZipBytes) {
    throw new Error(`ZIP exceeds the safety limit by ${blob.size - result.safetyLimits.maxEstimatedZipBytes} bytes`);
  }
  return {
    blob,
    fileName: safeName(result.outputManifest.zipFileName.replace(/\.zip$/i, ''), 'multi-package-labels') + '.zip',
    includedFiles: Object.keys(files),
  };
}

export function downloadArtifact(artifact: PrintRunArtifact) {
  const url = URL.createObjectURL(artifact.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = artifact.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
